import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import {
  consumeAuthReturnPath,
  getAccountDashboardPath,
  getPendingAuthReturnPath,
  getStoredUser,
  isAuthenticated,
  navigateToAuthDestination,
  storeAuthResult,
  UAE_PASS_REQUIRED_KEY,
} from "../auth";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";
import UaePassButton from "../component/UaePassButton";

const UAE_PASS_STATE_KEY = "tarahumUaePassState";
const UAE_PASS_NONCE_KEY = "tarahumUaePassNonce";
const UAE_PASS_RETURN_KEY = "tarahumUaePassReturnTo";
const UAE_PASS_RESTRICTED_KEY = "tarahumUaePassRestricted";
const UAE_PASS_RESTRICTED_PROFILE_KEY = "tarahumUaePassRestrictedProfile";

const defaultUaePassConfig = {
  authorizeUrl: "https://id.uaepass.ae/idshub/authorize",
  clientId: "tarahum_webr_prod",
  redirectUri: "",
  scope: "urn:uae:digitalid:profile:general",
  acrValues: "urn:safelayer:tws:policies:authentication:level:low",
};


const makeRandom = () => {
  const bytes = new Uint8Array(16);
  window.crypto?.getRandomValues?.(bytes);
  if (!bytes.some(Boolean)) {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

export default function Login({ language = "en" }) {
  const isArabic = language === "ar";
  const requiresUaePass = window.sessionStorage.getItem(UAE_PASS_REQUIRED_KEY) === "true";
  const interactions = useHomeInteractions();
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [loginStep, setLoginStep] = useState("credentials");
  const [otp, setOtp] = useState("");
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpSeconds, setOtpSeconds] = useState(0);
  const [resendSeconds, setResendSeconds] = useState(0);

  const uaePassConfig = useMemo(() => ({
    authorizeUrl: process.env.REACT_APP_UAE_PASS_AUTHORIZE_URL || defaultUaePassConfig.authorizeUrl,
    clientId: process.env.REACT_APP_UAE_PASS_CLIENT_ID || defaultUaePassConfig.clientId,
    redirectUri: process.env.REACT_APP_UAE_PASS_REDIRECT_URI || defaultUaePassConfig.redirectUri || `${window.location.origin}/uae-pass-callback`,
    scope: process.env.REACT_APP_UAE_PASS_SCOPE || defaultUaePassConfig.scope,
    acrValues: process.env.REACT_APP_UAE_PASS_ACR_VALUES || defaultUaePassConfig.acrValues,
  }), []);

  useEffect(() => {
    if (!isAuthenticated()) return;
    const accountType = getStoredUser()?.profile?.accounttype;
    const dashboardPath = getAccountDashboardPath(accountType);
    const destination = isArabic && dashboardPath === "/beneficent-dashboard" ? `/ar${dashboardPath}` : dashboardPath;
    navigateToAuthDestination(destination);
  }, [isArabic]);

  useEffect(() => {
    document.title = isArabic ? "تسجيل الدخول | مؤسسة تراحم الخيرية" : "Sign in | Tarahum Charity Foundation";
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic]);

  useEffect(() => {
    if (loginStep !== "otp") return undefined;
    const timer = window.setInterval(() => {
      setOtpSeconds((value) => Math.max(0, value - 1));
      setResendSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [loginStep]);

  const updateField = (field) => (event) => {
    const value = field === "remember" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!form.email.trim() || !form.password.trim()) {
      setMessage(isArabic ? "يرجى إدخال البريد الإلكتروني أو رقم الهاتف وكلمة المرور." : "Please enter your email address or mobile number and password.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(getApiUrl("/api/signinboard.ashx"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "login", login: form.email.trim(), password: form.password }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || (isArabic ? "تعذر تسجيل الدخول. يرجى التحقق من بيانات الدخول." : "Unable to sign in. Please check your credentials."));
      }

      if (result.token && !result.requiresOtp) {
        completeLogin(result);
        return;
      }

      if (!result.requiresOtp || !result.challengeId) {
        throw new Error(isArabic ? "تعذر بدء التحقق برمز OTP." : "Unable to start OTP verification.");
      }
      setOtpChallenge({ challengeId: result.challengeId, maskedMobile: result.maskedMobile || "" });
      setOtpSeconds(Number(result.expiresIn) || 300);
      setResendSeconds(Number(result.resendAfter) || 30);
      setOtp("");
      setLoginStep("otp");
      setMessage(result.message || "");
    } catch (error) {
      setMessage(error.message || (isArabic ? "تعذر تسجيل الدخول حالياً." : "Unable to sign in right now."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const completeLogin = (result) => {
    storeAuthResult(result, form.remember, result.loginMethod || "password");
    const dashboardPath = result.redirectTo || getAccountDashboardPath(result.profile?.accounttype);
    const destination = consumeAuthReturnPath(dashboardPath);
    navigateToAuthDestination(isArabic && /^\/beneficent-dashboard(?:[/?#]|$)/i.test(destination) ? `/ar${destination}` : destination);
  };

  const handleOtpVerification = async (event) => {
    event.preventDefault();
    setMessage("");
    if (!/^\d{6}$/.test(otp)) {
      setMessage(isArabic ? "يرجى إدخال رمز OTP المكون من 6 أرقام." : "Please enter the six-digit OTP.");
      return;
    }
    if (!otpChallenge?.challengeId) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(getApiUrl("/api/signinboard.ashx"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "verifyOtp", challengeId: otpChallenge.challengeId, otp }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to verify OTP.");
      completeLogin(result);
    } catch (error) {
      setMessage(error.message || (isArabic ? "تعذر التحقق من رمز OTP." : "Unable to verify OTP."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpChallenge?.challengeId || resendSeconds > 0 || isSubmitting) return;
    setMessage("");
    setIsSubmitting(true);
    try {
      const response = await fetch(getApiUrl("/api/signinboard.ashx"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "resendOtp", challengeId: otpChallenge.challengeId }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        if (result.resendAfter) setResendSeconds(Number(result.resendAfter));
        throw new Error(result.message || "Unable to resend OTP.");
      }
      setOtpChallenge((current) => ({ ...current, maskedMobile: result.maskedMobile || current.maskedMobile }));
      setOtpSeconds(Number(result.expiresIn) || 300);
      setResendSeconds(Number(result.resendAfter) || 30);
      setOtp("");
      setMessage(result.message || "");
    } catch (error) {
      setMessage(error.message || (isArabic ? "تعذر إعادة إرسال رمز OTP." : "Unable to resend OTP."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const changeLoginMethod = async () => {
    const challengeId = otpChallenge?.challengeId;
    setLoginStep("credentials");
    setOtpChallenge(null);
    setOtp("");
    setOtpSeconds(0);
    setResendSeconds(0);
    setMessage("");
    if (!challengeId) return;
    fetch(getApiUrl("/api/signinboard.ashx"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ action: "cancelOtp", challengeId }),
    }).catch(() => {});
  };

  const formatTimer = (seconds) => `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  const handleUaePassLogin = () => {
    setMessage("");
    window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_KEY);
    window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_PROFILE_KEY);

    if (!uaePassConfig.clientId || !uaePassConfig.redirectUri) {
      setMessage(isArabic ? "لم يتم إعداد UAE PASS بعد. يرجى التحقق من معرّف العميل ورابط إعادة التوجيه." : "UAE PASS is not configured yet. Please check the client ID and redirect URL.");
      return;
    }

    const state = `uaepass_${makeRandom()}`;
    const nonce = makeRandom();
    window.sessionStorage.setItem(UAE_PASS_STATE_KEY, state);
    window.sessionStorage.setItem(UAE_PASS_NONCE_KEY, nonce);
    const pendingReturnPath = getPendingAuthReturnPath();
    if (pendingReturnPath) {
      window.sessionStorage.setItem(UAE_PASS_RETURN_KEY, pendingReturnPath);
    } else {
      window.sessionStorage.removeItem(UAE_PASS_RETURN_KEY);
    }

    const params = new URLSearchParams({
      response_type: "code",
      client_id: uaePassConfig.clientId,
      redirect_uri: uaePassConfig.redirectUri,
      scope: uaePassConfig.scope,
      state,
      nonce,
    });

    if (uaePassConfig.acrValues) {
      params.set("acr_values", uaePassConfig.acrValues);
    }

    window.location.assign(`${uaePassConfig.authorizeUrl}?${params.toString()}`);
  };

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className={`login-mdn${isArabic ? " arabic-login-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          <div className="login-mdn-card">
            <div className="login-mdn-left">
              <div className="login-mdn-brand">
                  <div className="login-mdn-secure">{isArabic ? "دخول آمن" : "Secure Access"}</div>
              </div>

              <div className="login-mdn-mini">
                <div className="login-mdn-mini-box">
                  <strong>UAE</strong>
                  <span>{isArabic ? "تجربة دخول مفعّلة عبر UAE PASS" : "UAE PASS-enabled sign-in experience"}</span>
                </div>
                <div className="login-mdn-mini-box">
                  <strong>24/7</strong>
                  <span>{isArabic ? "وصول آمن إلى الحساب" : "Secure account accessibility"}</span>
                </div>
                <div className="login-mdn-mini-box">
                  <strong>100%</strong>
                  <span>{isArabic ? "واجهة حديثة ومتجاوبة" : "Responsive modern interface"}</span>
                </div>
              </div>
            </div>

            <div className="login-mdn-right">
              <div className="login-mdn-form">
                <div className="login-mdn-title-badge">
                  <span></span>
                  {isArabic ? "مرحباً بعودتك" : "Welcome Back"}
                </div>

                <h2>{loginStep === "otp" ? (isArabic ? "التحقق من رمز OTP" : "OTP Verification") : (isArabic ? "سجل الدخول للمتابعة" : "Sign in to Continue")}</h2>
                <p className="desc">{loginStep === "otp"
                  ? (isArabic ? "أدخل رمز التحقق المرسل إلى رقم الهاتف المرتبط بهوية UAE PASS." : "Enter the verification code sent to the mobile number linked to your UAE PASS identity.")
                  : requiresUaePass
                    ? (isArabic ? "يتطلب تقديم طلب المساعدة حساباً متصلاً بـ UAE PASS. سجّل الدخول عبر UAE PASS للعودة إلى طلبك." : "An Assistance Application requires an account connected with UAE PASS. Sign in with UAE PASS to return to your application.")
                    : (isArabic ? "سجل الدخول باستخدام البريد الإلكتروني أو رقم الهاتف، أو عبر UAE PASS." : "Sign in with your email address or mobile number, or with UAE PASS.")}</p>

                {requiresUaePass && loginStep === "credentials" ? (
                  <div className="login-mdn-message" role="status">
                    <i className="fa-solid fa-shield-halved"></i>{" "}
                    {isArabic ? "تم حفظ صفحة الطلب الحالية. بعد إكمال تسجيل الدخول عبر UAE PASS ستعود إليها تلقائياً." : "Your current application page has been saved. After UAE PASS sign-in, you will return to it automatically."}
                  </div>
                ) : null}

                {message ? (
                  <div className="login-mdn-message" role="alert">
                    {message}
                  </div>
                ) : null}

                {loginStep === "credentials" ? <form onSubmit={handleEmailLogin}>
                  <div className="login-mdn-group">
                    <label htmlFor="loginEmail">{isArabic ? "البريد الإلكتروني أو رقم الهاتف" : "Email Address or Mobile Number"}</label>
                    <div className="login-mdn-input">
                      <input
                        id="loginEmail"
                        type="text"
                        autoComplete="username"
                        placeholder={isArabic ? "أدخل البريد الإلكتروني أو رقم الهاتف" : "Enter email address or mobile number"}
                        value={form.email}
                        onChange={updateField("email")}
                        required
                      />
                      <span className="icon" aria-hidden="true">@</span>
                    </div>
                  </div>

                  <div className="login-mdn-group">
                    <label htmlFor="mdnPassword">{isArabic ? "كلمة المرور" : "Password"}</label>
                    <div className="login-mdn-input">
                      <input
                        id="mdnPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder={isArabic ? "أدخل كلمة المرور" : "Enter your password"}
                        value={form.password}
                        onChange={updateField("password")}
                        required
                      />
                      <button
                        className="icon login-mdn-icon-btn"
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        aria-label={showPassword ? (isArabic ? "إخفاء كلمة المرور" : "Hide password") : (isArabic ? "إظهار كلمة المرور" : "Show password")}
                      >
                        <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="login-mdn-options">
                    <label className="login-mdn-check">
                      <input type="checkbox" checked={form.remember} onChange={updateField("remember")} />
                      {isArabic ? "تذكرني" : "Remember me"}
                    </label>
                    <Link className="login-mdn-forgot" to={isArabic ? "/ar/forgot-password" : "/forgot-password"}>{isArabic ? "هل نسيت كلمة المرور؟" : "Forgot Password?"}</Link>
                  </div>

                  <button type="submit" className="login-mdn-submit" disabled={isSubmitting}>
                    {isSubmitting ? (isArabic ? "جارٍ تسجيل الدخول..." : "Signing in...") : (isArabic ? "تسجيل الدخول" : "Sign in")}
                  </button> 

                  <div className="login-mdn-divider mt-30">{isArabic ? "أو سجل الدخول عبر UAE PASS" : "or sign in with UAE PASS"}</div>

                  <UaePassButton flow="signIn" isArabic={isArabic} onClick={handleUaePassLogin} />

                </form> : (
                  <form className="login-otp-form" onSubmit={handleOtpVerification}>
                    <div className="login-otp-mobile">
                      <span>{isArabic ? "تم إرسال الرمز إلى" : "Code sent to"}</span>
                      <strong dir="ltr">{otpChallenge?.maskedMobile}</strong>
                      <small>{isArabic ? "لا يمكن تغيير رقم الهاتف أثناء تسجيل الدخول." : "This mobile number cannot be changed during sign-in."}</small>
                    </div>

                    <div className="login-mdn-group">
                      <label htmlFor="loginOtp">{isArabic ? "رمز OTP المكون من 6 أرقام" : "6-Digit OTP"}</label>
                      <div className="login-mdn-input login-otp-input">
                        <input
                          id="loginOtp"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          pattern="[0-9]{6}"
                          maxLength="6"
                          value={otp}
                          onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="000000"
                          required
                          autoFocus
                        />
                        <span className="icon" aria-hidden="true"><i className="fa-solid fa-shield-halved"></i></span>
                      </div>
                    </div>

                    <div className={`login-otp-timer${otpSeconds === 0 ? " expired" : ""}`}>
                      <span>{otpSeconds > 0 ? (isArabic ? "ينتهي الرمز خلال" : "OTP expires in") : (isArabic ? "انتهت صلاحية الرمز" : "OTP expired")}</span>
                      <strong dir="ltr">{formatTimer(otpSeconds)}</strong>
                    </div>

                    <button type="submit" className="login-mdn-submit" disabled={isSubmitting || otp.length !== 6 || otpSeconds === 0}>
                      {isSubmitting ? (isArabic ? "جارٍ التحقق..." : "Verifying...") : (isArabic ? "تحقق من الرمز" : "Verify OTP")}
                    </button>

                    <div className="login-otp-actions">
                      <button type="button" onClick={handleResendOtp} disabled={isSubmitting || resendSeconds > 0}>
                        {resendSeconds > 0
                          ? (isArabic ? `إعادة الإرسال خلال ${resendSeconds} ثانية` : `Resend OTP in ${resendSeconds}s`)
                          : (isArabic ? "إعادة إرسال الرمز" : "Resend OTP")}
                      </button>
                      <button type="button" onClick={changeLoginMethod}>{isArabic ? "تغيير طريقة تسجيل الدخول" : "Change Sign-in Method"}</button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
